import { useCooperantOrdersService } from '@/views/Orders/CooperantOrders/services/cooperantOrdersService'

export function createCooperantOrdersActions(state) {
    const cooperantOrdersService = useCooperantOrdersService()

    const fetchOrders = async (queryString = '') => {
        try {
            state.ordersLoading.value = true
            const response = await cooperantOrdersService.fetchOrders(queryString)
            state.orders.value = response.data.results || []
            return response
        } catch (error) {
            throw error
        } finally {
            state.ordersLoading.value = false
        }
    }

    const fetchOrder = async (orderId) => {
        try {
            state.ordersLoading.value = true
            const response = await cooperantOrdersService.fetchOrder(orderId)
            state.order.value = response.data
            state.initialOrderError.value = false
            state.initialOrderErrorId.value = null
            return response
        } catch (error) {
            throw error
        } finally {
            state.ordersLoading.value = false
        }
    }

    const fetchOrderLookups = async (orderNumbers) => {
        state.orderLookupsError.value = false
        state.orderLookupOrderNumbers.value = [...orderNumbers]
        state.initialOrderError.value = false
        state.initialOrderErrorId.value = null
        try {
            const response = await cooperantOrdersService.fetchByOrderNumbers(orderNumbers)
            state.orderLookups.value = response.data
            return response
        } catch (error) {
            state.orderLookupsError.value = true
            throw error
        }
    }

    const fetchOrderLookupsAndFirstOrder = async (orderNumbers) => {
        const response = await fetchOrderLookups(orderNumbers)
        const firstOrderLookup = response.data[0]

        if (!firstOrderLookup || firstOrderLookup.id === null) return response

        try {
            await fetchOrder(firstOrderLookup.id)
        } catch {
            state.initialOrderError.value = true
            state.initialOrderErrorId.value = firstOrderLookup.id
        }

        return response
    }

    const fetchCooperantDeliveryStatusNames = async () => {
        state.deliveryStatusesError.value = false
        try {
            const response = await cooperantOrdersService.fetchCooperantDeliveryStatusNames()
            state.cooperantDeliveryStatusNames.value = response.data.results
            return response
        } catch (error) {
            state.deliveryStatusesError.value = true
            throw error
        }
    }

    const fetchOrderStatuses = async () => {
        const response = await cooperantOrdersService.fetchOrderStatuses()
        state.orderStatuses.value = response.data.results || []
        return response
    }

    const addComment = async (commentData) => {
        try {
            return await cooperantOrdersService.addComment(commentData)
        } catch (error) {
            throw error
        }
    }

    const generateCooperantOrdersListReport = async (queryString = '', userEmail) => {
        try {
            return await cooperantOrdersService.generateCooperantOrdersListReport(queryString, userEmail)
        } catch (error) {
            throw error
        }
    }

    const printCooperantLabels = async ({ printingDeliveriesIds, carrierId }) => {
        try {
            const cooperationShipmentDeliveryPrint = printingDeliveriesIds
                .map((id) => {
                    const numId = parseInt(id, 10)
                    return Number.isFinite(numId) ? { deliveryId: numId } : null
                })
                .filter(Boolean)
            if (!cooperationShipmentDeliveryPrint.length) {
                throw new Error('Brak poprawnych identyfikatorów dostaw')
            }
            const payload = {
                cooperationShipmentDeliveryPrint,
                carrierId
            }
            return await cooperantOrdersService.printCooperantLabels(payload)
        } catch (error) {
            throw error
        }
    }

    const provideCooperationShipmentDeliveryNumber = async (payload) => {
        try {
            return await cooperantOrdersService.provideCooperationShipmentDeliveryNumber(payload)
        } catch (error) {
            throw error
        }
    }

    const updateOrder = async (orderData) => {
        try {
            state.ordersLoading.value = true
            return await cooperantOrdersService.updateOrder(orderData)
        } catch (error) {
            throw error
        } finally {
            state.ordersLoading.value = false
        }
    }

    const updateDelivery = async (deliveryId, payload) => {
        try {
            return await cooperantOrdersService.updateDelivery(deliveryId, payload)
        } catch (error) {
            throw error
        }
    }

    const deleteOrder = async (orderId) => {
        try {
            return await cooperantOrdersService.deleteOrder(orderId)
        } catch (error) {
            throw error
        }
    }

    const fetchToolboxTemplateContent = async (toolboxURI, templateId) => {
        try {
            return await cooperantOrdersService.fetchToolboxTemplateContent(toolboxURI, templateId)
        } catch (error) {
            throw error
        }
    }

    const setFilters = (filters) => {
        state.filters.value = { ...state.filters.value, ...filters }
    }

    const resetFilters = () => {
        state.filters.value = state.createInitialFilters()
    }

    return {
        fetchOrders,
        fetchOrder,
        fetchOrderLookups,
        fetchOrderLookupsAndFirstOrder,
        fetchCooperantDeliveryStatusNames,
        fetchOrderStatuses,
        addComment,
        generateCooperantOrdersListReport,
        printCooperantLabels,
        provideCooperationShipmentDeliveryNumber,
        updateOrder,
        updateDelivery,
        deleteOrder,
        fetchToolboxTemplateContent,
        setFilters,
        resetFilters
    }
}
